import { useEffect, useRef, useState } from 'react';

export interface AudioPlayerState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  activeVoiceId: string | null;
}

export interface AudioPlayerControls {
  play: (voiceId: string, url: string) => Promise<void>;
  pause: () => void;
  resume: () => Promise<void>;
  seek: (time: number) => void;
  reset: () => void;
}

export function useAudioPlayer(): [AudioPlayerState, AudioPlayerControls] {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [state, setState] = useState<AudioPlayerState>({
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    activeVoiceId: null,
  });

  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'metadata';
    audioRef.current = audio;

    const syncTime = () => {
      setState((current) => ({
        ...current,
        currentTime: audio.currentTime,
        duration: Number.isFinite(audio.duration) ? audio.duration : current.duration,
      }));
    };

    const onPause = () => {
      setState((current) => ({ ...current, isPlaying: false }));
    };

    const onPlay = () => {
      setState((current) => ({ ...current, isPlaying: true }));
    };

    const onEnded = () => {
      setState((current) => ({
        ...current,
        isPlaying: false,
        currentTime: 0,
      }));
    };

    const onLoadedMetadata = () => {
      setState((current) => ({
        ...current,
        duration: Number.isFinite(audio.duration) ? audio.duration : current.duration,
      }));
    };

    audio.addEventListener('timeupdate', syncTime);
    audio.addEventListener('durationchange', syncTime);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);

    return () => {
      audio.pause();
      audio.src = '';
      audio.removeEventListener('timeupdate', syncTime);
      audio.removeEventListener('durationchange', syncTime);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
  }, []);

  const play = async (voiceId: string, url: string) => {
    const audio = audioRef.current;
    if (!audio) return;

    if (state.activeVoiceId !== voiceId || audio.src !== url) {
      audio.pause();
      audio.src = url;
      audio.load();
      setState({
        isPlaying: false,
        currentTime: 0,
        duration: 0,
        activeVoiceId: voiceId,
      });
    }

    await audio.play();
  };

  const pause = () => {
    audioRef.current?.pause();
  };

  const resume = async () => {
    if (!audioRef.current) return;
    await audioRef.current.play();
  };

  const seek = (time: number) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = time;
    setState((current) => ({ ...current, currentTime: time }));
  };

  const reset = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.src = '';
    setState({
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      activeVoiceId: null,
    });
  };

  return [state, { play, pause, resume, seek, reset }];
}
